import { useEffect } from 'react';

import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import { appPath } from '../lib/config';
import '../components/landing.css';
import './getting-started.css';

const PAGE_TITLE = 'How to upload a MyChron session | Paddiq';

export function UploadMyChronPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">Sessions guide</p>
            <h1>Upload MyChron sessions on the web.</h1>
            <p className="guide-hero__lead">
              Upload one or more MyChron files, check that each run matches the right circuit,
              assign the driver and vehicle, and open finished sessions with their laps ready.
            </p>
            <div className="guide-hero__actions">
              <a
                className="landing-btn landing-btn--primary landing-btn--lg"
                href={appPath('/sessions')}
              >
                Open Sessions
              </a>
              <a
                className="landing-btn landing-btn--ghost landing-btn--lg"
                href="/resources/getting-started"
              >
                View getting started
              </a>
            </div>
            <p className="guide-hero__meta">Web guide · Reviewed October 1, 2026</p>
          </div>
        </section>

        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Guide summary">
              <ResourceSidebar currentPath="/resources/upload-mychron-session" />
              <p className="guide-summary__label">In this guide</p>
              <ol>
                <li>
                  <a href="#prepare">Prepare the files</a>
                </li>
                <li>
                  <a href="#choose-files">Choose and inspect files</a>
                </li>
                <li>
                  <a href="#match-circuits">Match circuits</a>
                </li>
                <li>
                  <a href="#assign-import">Assign and import</a>
                </li>
                <li>
                  <a href="#review">Review the sessions</a>
                </li>
              </ol>
              <div className="guide-summary__note">
                <strong>Before you start</strong>
                <span>
                  Choosing files only checks them. Nothing uploads until you select Import.
                </span>
              </div>
            </aside>

            <article className="guide-content">
              <section className="guide-step" id="prepare">
                <div className="guide-step__number" aria-hidden="true">
                  1
                </div>
                <div>
                  <p className="guide-step__eyebrow">Before upload</p>
                  <h2>Choose the sessions you want to upload.</h2>
                  <p>
                    Need to get sessions off your logger first? Use the{' '}
                    <a href="/resources/paddiq-link">Paddiq Link guide for Mac and Windows</a> or
                    the <a href="/resources/import-mychron-iphone">iPhone import guide</a>.
                  </p>
                  <p>Paddiq accepts a MyChron export with one of these file extensions:</p>
                  <div className="guide-file-types" aria-label="Supported MyChron upload types">
                    <span>JSON</span>
                    <span>XRZ</span>
                    <span>XRK</span>
                    <span>HRZ</span>
                    <span>HRK</span>
                    <span>DRK</span>
                  </div>
                  <p>
                    Each uploaded file must be 100 MB or smaller. If your source is GPX, choose{' '}
                    <strong>Uploading GPX, CSV, or another file?</strong> instead of adding it to
                    the MyChron queue.
                  </p>
                  <div className="guide-callout">
                    Create the driver and vehicle in Team and Garage first when you can. You may
                    still import with either assignment set to Unassigned and correct it later.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="choose-files">
                <div className="guide-step__number" aria-hidden="true">
                  2
                </div>
                <div>
                  <p className="guide-step__eyebrow">Choose files</p>
                  <h2>Open the importer and check the queue.</h2>
                  <ol className="guide-instructions">
                    <li>Sign in to the correct Paddiq workspace.</li>
                    <li>
                      Open <strong>Sessions → All sessions</strong>.
                    </li>
                    <li>
                      Select <strong>Upload</strong> in the table header.
                    </li>
                    <li>
                      Drop one or more files into the MyChron importer, or select{' '}
                      <strong>Choose files</strong>.
                    </li>
                    <li>
                      Review the filename, recorded date, file size, detected venue, logger laps,
                      and channels for every session that Paddiq can read.
                    </li>
                  </ol>
                  <p>
                    Select <strong>Add more</strong> to choose additional files, or{' '}
                    <strong>Remove</strong> beside a file that should not be imported. If one file
                    cannot be read, valid files remain in the queue.
                  </p>
                  <div className="guide-callout">
                    If Upload is not available, finish any workspace access or billing step shown in
                    Paddiq before trying again.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="match-circuits">
                <div className="guide-step__number" aria-hidden="true">
                  3
                </div>
                <div>
                  <p className="guide-step__eyebrow">Match circuits</p>
                  <h2>Confirm the circuit for each file.</h2>
                  <p>
                    Paddiq compares the recorded GPS with saved circuits and may preselect a
                    recommendation. Select each file in the queue and compare the purple session lap
                    with the cyan saved-circuit shape.
                  </p>
                  <ol className="guide-instructions">
                    <li>Keep the recommendation when the shapes represent the same layout.</li>
                    <li>Choose another saved circuit when it is a better match.</li>
                    <li>
                      Choose <strong>Leave circuit unassigned</strong> when no option is
                      trustworthy.
                    </li>
                  </ol>
                  <p>
                    Circuit choices are saved per file. A confirmed circuit is assigned and used to
                    calculate complete circuit laps during import. An unassigned session still keeps
                    the raw laps reported by the MyChron, and you can set up circuit laps later.
                  </p>
                  <div className="guide-callout">
                    A recommendation is not proof. Check the shape and layout before continuing;
                    Paddiq does not silently assign a circuit you have not confirmed.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="assign-import">
                <div className="guide-step__number" aria-hidden="true">
                  4
                </div>
                <div>
                  <p className="guide-step__eyebrow">Assign &amp; import</p>
                  <h2>Choose the driver and vehicle, then import.</h2>
                  <p>
                    Choose the <strong>Driver</strong> and <strong>Vehicle</strong>, or leave either
                    value <strong>Unassigned</strong>. These two choices apply to every file in the
                    upload and can be edited later in the Sessions table.
                  </p>
                  <p>
                    Choosing a driver filters the Vehicle list to unassigned vehicles and vehicles
                    assigned to that driver. If the vehicle you need is missing, check its Garage
                    assignment or clear the driver choice.
                  </p>
                  <ol className="guide-instructions">
                    <li>
                      Select the <strong>Import</strong> button, such as{' '}
                      <strong>Import 1 session</strong> or <strong>Import 3 sessions</strong>.
                    </li>
                    <li>Keep the page open until all files finish uploading.</li>
                    <li>
                      Watch the progress shown for each file. Paddiq saves the session and
                      calculates its circuit laps when a circuit is assigned.
                    </li>
                  </ol>
                  <p>
                    If part of a batch fails, do not immediately import the failed queue again. A
                    file can already exist in Sessions even when later circuit processing fails.
                    Exit the wizard and refresh Sessions first. If the run exists, keep that session
                    and finish its circuit setup there. Retry the original file only when no session
                    was created.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="review">
                <div className="guide-step__number" aria-hidden="true">
                  5
                </div>
                <div>
                  <p className="guide-step__eyebrow">Session review</p>
                  <h2>Open each finished session and verify it.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Wait for the <strong>session is ready</strong> or{' '}
                      <strong>sessions are ready</strong> step.
                    </li>
                    <li>
                      For a circuit-assigned run, review its circuit and lap count plus the best lap
                      when one was calculated. An unassigned run instead shows that raw laps are
                      available.
                    </li>
                    <li>
                      Select <strong>Open session</strong> beside the run you want to inspect, or
                      select <strong>Done</strong> to return to the session library.
                    </li>
                    <li>Confirm the saved driver, vehicle, and circuit.</li>
                    <li>
                      Review Raw laps and, when a circuit was assigned, Circuit laps. Check the map
                      and telemetry channels available in the source, then switch to Traces View
                      when you want detailed charts.
                    </li>
                  </ol>
                  <div className="guide-ready">
                    <h3>The upload is ready when:</h3>
                    <ul>
                      <li>the run appears in Sessions;</li>
                      <li>its driver and vehicle are correct, or intentionally Unassigned;</li>
                      <li>its circuit is correct, or intentionally left unassigned;</li>
                      <li>the expected laps and channels from the source file are available.</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="guide-troubleshooting" id="troubleshooting">
                <p className="guide-step__eyebrow">Troubleshooting</p>
                <h2>If the upload is not ready</h2>
                <p>
                  For a complete decision guide covering read-only access, rejected files,
                  assignment corrections, and circuit laps, see{' '}
                  <a className="guide-text-link" href="/resources/troubleshoot-session-uploads">
                    Troubleshoot session uploads and assignments
                  </a>
                  .
                </p>
                <div className="guide-troubleshooting__grid">
                  <article>
                    <h3>The file cannot be selected</h3>
                    <p>
                      Confirm its extension is JSON, XRZ, XRK, HRZ, HRK, or DRK. An exported JSON
                      file must contain a MyChron session. Use the other-file link for GPX. CSV and
                      other supporting files are not native MyChron session exports.
                    </p>
                  </article>
                  <article>
                    <h3>The file is too large</h3>
                    <p>
                      Each uploaded file must fit the 100 MB limit. Keep the original export and
                      contact support if it is too large; do not remove telemetry by hand.
                    </p>
                  </article>
                  <article>
                    <h3>The intended vehicle is missing</h3>
                    <p>
                      Clear or reselect the Driver field. Paddiq filters the list to unassigned
                      vehicles and vehicles assigned to the chosen driver.
                    </p>
                  </article>
                  <article>
                    <h3>No circuit is recommended</h3>
                    <p>
                      Import with the circuit unassigned, open the new session, and complete Circuit
                      setup later. Raw MyChron laps remain available when the logger recorded them.
                    </p>
                  </article>
                </div>
              </section>

              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">Ready to upload?</p>
                  <h2>Upload your next session.</h2>
                  <p>Choose your files, check the circuit, and select Import.</p>
                </div>
                <div className="guide-cta__actions">
                  <a
                    className="landing-btn landing-btn--primary landing-btn--lg"
                    href={appPath('/sessions')}
                  >
                    Open Sessions
                  </a>
                  <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                    Contact support
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
